import React, { useRef, useState } from "react";
import { ref, uploadBytesResumable, getDownloadURL } from "firebase/storage";
import { FileText, ImagePlus, Trash2, UploadCloud } from "lucide-react";
import toast from "react-hot-toast";
import { storage, deleteObjectByUrl } from "../../Firebase/Firebase.utils";
import { storagePath } from "../utils/cmsSchema";

const ACCEPTS = {
  image: "image/png,image/jpeg,image/jpg,image/webp,image/gif",
  pdf: "application/pdf",
};

const SIZE_LIMITS = {
  image: 10 * 1024 * 1024,
  pdf: 25 * 1024 * 1024,
};

function fileNameFromUrl(url) {
  if (!url) return "";
  try {
    const u = new URL(url);
    const last = decodeURIComponent(u.pathname.split("/").pop() || "");
    return last.split("/").pop();
  } catch {
    return url;
  }
}

export default function FileDropzone({
  label,
  kind = "image",
  collection,
  docId,
  value,
  onChange,
  onUploadingChange,
  required = false,
}) {
  const inputRef = useRef(null);
  const [progress, setProgress] = useState(0);
  const [isUploading, setIsUploading] = useState(false);
  const [isDragging, setIsDragging] = useState(false);

  const accept = ACCEPTS[kind];
  const limit = SIZE_LIMITS[kind];

  const setUploading = (b) => {
    setIsUploading(b);
    onUploadingChange?.(b);
  };

  const handleFiles = (files) => {
    if (!files || files.length === 0) return;
    const file = files[0];
    if (limit && file.size > limit) {
      toast.error(
        `File too large. Max ${(limit / 1024 / 1024).toFixed(0)} MB.`
      );
      return;
    }
    if (kind === "image" && !file.type.startsWith("image/")) {
      toast.error("Please choose an image file.");
      return;
    }
    if (kind === "pdf" && file.type !== "application/pdf") {
      toast.error("Please choose a PDF file.");
      return;
    }

    const path = storagePath({
      collection,
      docId,
      kind: kind === "image" ? "hero" : "pdf",
      filename: `${Date.now()}-${file.name}`,
    });
    const storageRef = ref(storage, path);
    const task = uploadBytesResumable(storageRef, file);
    setUploading(true);
    setProgress(0);

    task.on(
      "state_changed",
      (snap) => {
        setProgress((snap.bytesTransferred / snap.totalBytes) * 100);
      },
      (error) => {
        console.error(error);
        toast.error("Upload failed.");
        setUploading(false);
        setProgress(0);
      },
      async () => {
        const url = await getDownloadURL(task.snapshot.ref);
        onChange?.(url);
        setUploading(false);
        setProgress(100);
        toast.success(`${kind === "image" ? "Image" : "PDF"} uploaded.`);
      }
    );
  };

  const handleRemove = async () => {
    if (!value) return;
    try {
      await deleteObjectByUrl(value);
    } catch {
      // best-effort
    }
    onChange?.("");
  };

  const onDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    handleFiles(e.dataTransfer.files);
  };

  return (
    <div>
      {label ? (
        <label className="cms-eyebrow block mb-1.5 text-gray-600">
          {label}
          {required ? <span className="text-leechiuOrange ml-0.5">*</span> : null}
        </label>
      ) : null}

      {value && !isUploading ? (
        <div className="rounded-xl border border-gray-200 bg-white overflow-hidden">
          {kind === "image" ? (
            <div className="relative aspect-[16/9] bg-gray-50">
              <img
                src={value}
                alt=""
                className="w-full h-full object-cover"
              />
            </div>
          ) : (
            <div className="flex items-center gap-3 p-4 bg-gray-50">
              <div className="w-10 h-10 rounded-lg bg-leechiuBlue/10 text-leechiuBlue flex items-center justify-center">
                <FileText className="w-5 h-5" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium text-gray-800 truncate">
                  {fileNameFromUrl(value)}
                </div>
                <a
                  href={value}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-xs text-leechiuBlue hover:underline"
                >
                  View PDF
                </a>
              </div>
            </div>
          )}
          <div className="flex items-center justify-end gap-2 p-2 border-t border-gray-100 bg-white">
            <button
              type="button"
              onClick={() => inputRef.current?.click()}
              className="text-xs text-gray-600 hover:bg-gray-50 px-2 py-1 rounded-md inline-flex items-center gap-1"
            >
              <UploadCloud className="w-3.5 h-3.5" />
              Replace
            </button>
            <button
              type="button"
              onClick={handleRemove}
              className="text-xs text-red-600 hover:bg-red-50 px-2 py-1 rounded-md inline-flex items-center gap-1"
            >
              <Trash2 className="w-3.5 h-3.5" />
              Remove
            </button>
          </div>
        </div>
      ) : (
        <div
          onDragOver={(e) => {
            e.preventDefault();
            setIsDragging(true);
          }}
          onDragLeave={() => setIsDragging(false)}
          onDrop={onDrop}
          onClick={() => !isUploading && inputRef.current?.click()}
          className={`rounded-xl border-2 border-dashed p-6 cursor-pointer transition-colors text-center ${
            isDragging
              ? "border-leechiuOrange bg-orange-50/40"
              : "border-gray-200 bg-gray-50 hover:border-leechiuBlue/40 hover:bg-white"
          }`}
        >
          {isUploading ? (
            <div className="flex flex-col items-center gap-2">
              <div className="text-sm text-gray-700">
                Uploading… {Math.round(progress)}%
              </div>
              <div className="w-full max-w-xs h-1.5 rounded-full bg-gray-200 overflow-hidden">
                <div
                  className="h-full bg-leechiuOrange transition-all"
                  style={{ width: `${progress}%` }}
                />
              </div>
            </div>
          ) : (
            <div className="flex flex-col items-center gap-1.5">
              <div className="w-10 h-10 rounded-full bg-white border border-gray-200 flex items-center justify-center text-leechiuBlue">
                {kind === "image" ? (
                  <ImagePlus className="w-5 h-5" />
                ) : (
                  <FileText className="w-5 h-5" />
                )}
              </div>
              <div className="text-sm font-medium text-gray-700">
                Drop {kind === "image" ? "an image" : "a PDF"} here, or click to upload
              </div>
              <div className="text-xs text-gray-400">
                {kind === "image"
                  ? "PNG, JPG, WEBP up to 10 MB"
                  : "PDF up to 25 MB"}
              </div>
            </div>
          )}
        </div>
      )}

      <input
        ref={inputRef}
        type="file"
        accept={accept}
        className="hidden"
        onChange={(e) => handleFiles(e.target.files)}
      />
    </div>
  );
}
